import React, { useCallback, useEffect, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import Context from 'components/Pages/FileLibrary/context';
import useAssetsPermissions from 'hooks/useAssetsPermissions';
import { useSnackbar } from 'hooks/useSnackbar';
import { clearAssetsList, setOwnershipAction, updateAssetAction, updateSearch } from 'store/admin/files';
import { assetsSelector, isGlobalLoaderSelector, searchSelector } from 'store/admin/files/selectors';
import { searchFiltersSelector } from 'store/admin/files/selectors';
import { deleteThunk, loadAssetsListThunk } from 'store/admin/files/thunks';
import { editThunk, uploadThunk } from 'store/assets/thunks';
import { confirmAlert } from 'utils/alert';
import { debounce } from 'utils/debounce';

import CommonLibrary from './common';
import { checkMediaItemIsUsed } from './functions';

const FileLibrary = (props) => {
  const MODULE = props.module;
  const dispatch = useDispatch();

  const assets = useSelector(assetsSelector(MODULE)) || null;
  const isGlobalLoader = useSelector(isGlobalLoaderSelector) || false;
  const search = useSelector(searchSelector) || '';
  const { media } = useSelector(searchFiltersSelector);

  const select = (media, document) => (MODULE === 'media' ? media : document);

  const onLoadChunk = useCallback(
    (loader, force = false, resetPage = false) => {
      return dispatch(loadAssetsListThunk(MODULE, loader, force, resetPage));
    },
    [MODULE, dispatch],
  );

  const onLoadChunkDebounced = useMemo(() => {
    return debounce(() => onLoadChunk(true, true, true), 300);
  }, [onLoadChunk]);

  const onSearchType = (value) => {
    dispatch(updateSearch(value));
    onLoadChunkDebounced();
  };

  const onSnackbar = useSnackbar();
  const onSelectFile = async (asset) => {
    await dispatch(
      editThunk({
        id: asset.id,
        onAfterEdited: (asset) => dispatch(updateAssetAction(asset)),
        onDelete,
      }),
    );
    // updated image will be refreshed
    // todo: need to rewrite this block
    const img = document.querySelector(`img[src*="assets/${asset.id}/"]`);

    if (img) {
      img.src = img.src + '?r=' + Math.random();
    }
  };

  const defaults = {};
  if (MODULE === 'media') {
    defaults.publiclyAvailable = true;
  }

  const { withAssetSettings } = useAssetsPermissions();

  const onOpenModal = async () => {
    const assets = await dispatch(
      uploadThunk({
        modalTitle: select('Add New Media', 'Add New Documents'),
        dropZonePlaceholder: select('Click to Upload File', 'No Document Files Found'),
        namePlaceholder: select('Elephant Family', 'Standard Terms & Conditions'),
        tagsPlaceholder: select('Elephant', 'Cancellation Schedule'),
        type: MODULE,
        multiple: true,
        defaults,
        withAssetSettings,
      }),
    );

    if (assets === null) {
      return;
    }

    onLoadChunk(true, true, true);
  };
  const onDelete = async (asset) => {
    const isUsed = await checkMediaItemIsUsed(asset.id);

    const mediaText =
      MODULE === 'media'
        ? 'Warning! This image is being used on an active page. If you remove it from here, it will also be removed from any pages using it'
        : 'Warning! This document is attached to an active page. If you remove it from here, it will be removed from any active pages using it.';

    return confirmAlert({
      title: isUsed ? (
        <>
          <span style={{ color: 'var(--danger_color)' }}>{mediaText}</span>
        </>
      ) : (
        'Warning'
      ),
      hintBottom: (
        <>
          This content will no longer available.
          <br />
          <strong>Are you sure you want to delete this asset?</strong>
          <br />
        </>
      ),
      confirmButtonText: 'Delete',
      onConfirm: async () => {
        const type = MODULE === 'media' ? 'photo' : 'file';
        const name = asset.name;

        try {
          await dispatch(deleteThunk(MODULE, asset.id));
          onSnackbar(`The ${type} '${name}' was deleted.`);
        } catch {
          onSnackbar(`The ${type} '${name}' couldn't be deleted. Please try again.`, true);
        }
      },
    });
  };

  useEffect(() => {
    onLoadChunk(true, true, true);

    return () => {
      dispatch(setOwnershipAction('all'));
      dispatch(clearAssetsList(true));
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, media]);

  const value = {
    assets,
    isGlobalLoader,
    search,
    onSearchType,
    onLoadChunk,
    onSelectFile,
    onDelete,
    onOpenModal,
    ...props,
  };

  return (
    <Context.Provider value={value}>
      <CommonLibrary />
    </Context.Provider>
  );
};

export default FileLibrary;
